import { motion } from "framer-motion";
import { Instagram, Linkedin, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";

interface SiteSettings {
  instagram_url: string | null;
  linkedin_url: string | null;
  whatsapp_url: string | null;
  email: string | null;
}

export const Footer = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("instagram_url, linkedin_url, whatsapp_url, email")
        .single();
      if (data) setSettings(data);
    };
    fetchSettings();
  }, []);

  const email = settings?.email || "sotsekadvocacia@gmail.com";
  const googleMapsUrl = "https://www.google.com/maps/search/?api=1&query=R.+Francisco+Leit%C3%A3o,+653+-+Pinheiros,+S%C3%A3o+Paulo,+SP";

  return (
    <footer className="py-12 border-t border-border">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.02 }}
          >
            <span className="font-serif text-2xl font-medium tracking-tight">
              <span className="text-foreground">Sotsek</span>
              <span className="text-primary ml-1">Advocacia</span>
            </span>
          </motion.a>

          {/* Contact Info & SEO Text */}
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <p className="text-xs text-muted-foreground/70 italic">
              Escritório especializado em direito tributário
            </p>
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-foreground transition-colors"
            >
              <span>R. Francisco Leitão, 653 - Pinheiros, SP</span>
            </a>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            <motion.a
              href={settings?.instagram_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Seguir no Instagram"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Instagram className="w-4 h-4" aria-hidden="true" />
            </motion.a>
            <motion.a
              href={settings?.linkedin_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Seguir no LinkedIn"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Linkedin className="w-4 h-4" aria-hidden="true" />
            </motion.a>
            <motion.a
              href={settings?.whatsapp_url || "http://wa.me/+5511975489900"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contato via WhatsApp"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" aria-hidden="true" />
            </motion.a>
            <motion.a
              href={`mailto:${email}`}
              aria-label="Enviar email"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Mail className="w-4 h-4" aria-hidden="true" />
            </motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
};
